'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'
import {
  Loader2, GraduationCap, Shield, ArrowRight, MapPin, Phone, Mail,
  ChevronDown, CheckCircle, AlertCircle, Car, CreditCard, Building2, Banknote,
  Star, Clock, Users, BookOpen, MessageSquare, ChevronLeft, GitBranch,
  User, Hash, Calendar, Home, ChevronRight, Coins, Trophy, BadgeCheck, FileText, Globe,
} from 'lucide-react'
import { api } from '@/lib/api'
import { useCategoriasLicenca, buildCategoriaLicencaMap } from '@/lib/system-config'
import { getApiErrorMessage } from '@/lib/getApiErrorMessage'

// ── Tipos ─────────────────────────────────────────────────────────────────────

interface LandingBackground {
  type: 'solid' | 'gradient' | 'image' | 'transparent'
  color?: string
  gradient?: { from: string; to: string; direction: string }
  imageUrl?: string
  imagePosition?: string
  imageSize?: 'cover' | 'contain' | 'auto'
  imageRepeat?: 'no-repeat' | 'repeat' | 'repeat-x' | 'repeat-y'
  imageFixed?: boolean
  imageBlur?: number
  overlayColor?: string
  overlayOpacity?: number
}

interface SectionDivider {
  type: 'none' | 'wave' | 'angle' | 'curve' | 'zigzag'
  color?: string
  height?: number
  flip?: boolean
}

interface LandingCardStyle {
  bgColor?: string
  borderColor?: string
  borderRadius?: number
  shadow?: 'none' | 'sm' | 'md' | 'lg'
  textColor?: string
  accentColor?: string
}

interface LandingSectionConfig {
  key: string
  visible: boolean
  order: number
  variant?: string
  background?: LandingBackground
  padding?: { top: number; bottom: number }
  textColor?: 'light' | 'dark'
  containerWidth?: 'normal' | 'wide' | 'full'
  headingColor?: string
  bodyColor?: string
  accentColor?: string
  headingFont?: string
  cardStyle?: LandingCardStyle
  dividerBottom?: SectionDivider
  texts?: Record<string, string>
}

interface LandingHeroButton {
  id: string
  text: string
  url: string
  style: 'primary' | 'secondary' | 'outline' | 'ghost'
  visible: boolean
  order: number
  newTab?: boolean
}

interface LandingSlide {
  imageUrl: string
  title?: string
  subtitle?: string
  textPosition?: 'left' | 'center' | 'right'
  textVertical?: 'top' | 'middle' | 'bottom'
  titleColor?: string
  subtitleColor?: string
  titleSize?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  overlayColor?: string
  overlayOpacity?: number
  cta?: { text: string; url: string; style?: 'primary' | 'secondary' | 'outline' }
  ctaButtons?: LandingHeroButton[]
  slideHideButtons?: boolean
  duration?: number
  hideDefaultText?: boolean
}

interface LandingContactBlock {
  id: string
  type: 'location' | 'phone' | 'email' | 'whatsapp' | 'hours' | 'website'
  visible: boolean
  order: number
  label?: string
  value?: string
  bgType?: 'theme' | 'solid' | 'image'
  bgColor?: string
  bgImageUrl?: string
  textColor?: 'auto' | 'light' | 'dark'
  mapEmbed?: boolean
  mapZoom?: number
}

interface LandingContactConfig {
  title?: string
  subtitle?: string
  layout?: 'cards' | 'map-hero' | 'map-sidebar' | 'horizontal'
  blocks?: LandingContactBlock[]
}

interface LandingConfig {
  colorPrimary: string | null
  colorSecondary: string | null
  colorAccent: string | null
  fontConfig?: { heading: string; body: string } | null
  themePreset?: string | null
  heroCtaText: string | null
  heroSecondaryCtaText: string | null
  heroShowTextWithSlides?: boolean
  heroHideButtons?: boolean
  heroButtons?: LandingHeroButton[]
  logoSize?: number | null
  slides?: LandingSlide[]
  sectionsConfig: LandingSectionConfig[]
  aboutTitle: string | null
  aboutText: string | null
  aboutImageUrl: string | null
  statsItems?: { value: string; label: string; icon?: string }[]
  instructors?: { id: string; nome: string; cargo?: string; fotoUrl?: string; bio?: string }[]
  testimonials: { nome: string; texto: string; fotoUrl?: string; estrelas?: number }[]
  faqItems: { pergunta: string; resposta: string }[]
  howItWorksSteps: { titulo: string; descricao: string }[]
  customBlocks?: { id: string; type: string; afterSection?: string; background?: LandingBackground; config: Record<string, any> }[]
  footerDescription: string | null
  socialLinks: Record<string, string>
  footerSchedule: string | null
  footerLinks: { label: string; url: string }[]
  whatsappConfig?: { enabled: boolean; number: string; message?: string; position?: string; label?: string } | null
  seoConfig?: { title?: string; description?: string; ogImage?: string; noIndex?: boolean } | null
  contactConfig?: LandingContactConfig | null
}

interface SucursalPublica {
  id: string; nome: string; morada: string | null; zonaId: string | null
}

interface ZonaPublica {
  id: string; nome: string
}

export interface PortalInfo {
  id: string; name: string; slug: string
  pais: string
  moeda: { codigo: string; simbolo: string; locale: string; decimais: number; posicao: 'antes' | 'depois' }
  logoUrl: string | null; faviconUrl: string | null
  slideshowImages: { url: string; titulo?: string; descricao?: string }[]
  address: string | null; phone: string | null; email: string | null
  landingPageEnabled: boolean
  landingShowForm: boolean
  landingHeroTitle: string | null
  landingHeroSubtitle: string | null
  categorias: string[]
  valoresPorCategoria: Record<string, number>
  nomesCategorias: Record<string, string>
  maxParcelas: number
  camposObrigatorios: string[]
  camposInternos: string[]
  camposLanding: string[]
  metodosAceites: string[]
  transferenciaIban: string | null
  transferenciaBanco: string | null
  transferenciaTitular: string | null
  controleVagas: boolean
  sucursais: SucursalPublica[]
  zonas: ZonaPublica[]
  documentosLanding: string[]
  landingConfig: LandingConfig | null
}

/** Devolve o nome de uma categoria: override da escola > mapa global da BD > código */
function getNomeCategoria(cat: string, overrides: Record<string, string> = {}, globalMap: Record<string, string> = {}): string {
  return overrides[cat] || globalMap[cat] || ''
}

function fmtCVE(v: number, moeda?: { codigo: string; simbolo: string; locale: string; decimais: number; posicao: 'antes' | 'depois' }) {
  if (!v) return 'Sob consulta'
  const m = moeda ?? { codigo: 'CVE', simbolo: 'ECV', locale: 'pt-PT', decimais: 0, posicao: 'depois' as const }
  const n = new Intl.NumberFormat(m.locale, { minimumFractionDigits: m.decimais, maximumFractionDigits: m.decimais }).format(v)
  return m.posicao === 'antes' ? `${m.simbolo} ${n}` : `${n} ${m.simbolo}`
}

// ── Cores helpers ─────────────────────────────────────────────────────────────

function getColors(cfg: LandingConfig | null) {
  return {
    primary:   cfg?.colorPrimary   || '#F06823',
    secondary: cfg?.colorSecondary || '#1e3a5f',
    accent:    cfg?.colorAccent    || '#F06823',
  }
}

function hexToRgb(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  return `${r},${g},${b}`
}

// ── Secções da landing page ───────────────────────────────────────────────────

const DEFAULT_SECTIONS: { key: string; visible: boolean; order: number }[] = [
  { key: 'categories',   visible: true,  order: 0 },
  { key: 'howItWorks',   visible: true,  order: 1 },
  { key: 'about',        visible: false, order: 2 },
  { key: 'form',         visible: true,  order: 3 },
  { key: 'testimonials', visible: false, order: 4 },
  { key: 'payment',      visible: true,  order: 5 },
  { key: 'faq',          visible: false, order: 6 },
  { key: 'contact',      visible: true,  order: 7 },
]

function getOrderedSections(cfg: LandingConfig | null): { key: string; visible: boolean; order: number }[] {
  const sections = (cfg?.sectionsConfig && cfg.sectionsConfig.length > 0) ? cfg.sectionsConfig : DEFAULT_SECTIONS
  return [...sections].sort((a, b) => a.order - b.order)
}

// ── Ícones sociais (SVG inline) ───────────────────────────────────────────────

function SocialIcon({ platform, className }: { platform: string; className?: string }) {
  const cn = className || 'w-5 h-5'
  const paths: Record<string, string> = {
    facebook: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
    instagram: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z',
    youtube: 'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
    whatsapp: 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z',
    tiktok: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
  }
  if (!paths[platform]) return null
  return <svg viewBox="0 0 24 24" fill="currentColor" className={cn}><path d={paths[platform]} /></svg>
}

// ── Documentos ───────────────────────────────────────────────────────────────

const DOCUMENTOS_LABELS: Record<string, string> = {
  cni:                     'Cópia de CNI / BI',
  passaporte:              'Cópia de Passaporte',
  registo_criminal:        'Certidão de Registo Criminal',
  comprovativo_residencia: 'Comprovativo de Residência',
  foto_passe:              'Fotografia tipo passe',
}

// ── Formulário de pré-inscrição ───────────────────────────────────────────────

const FORM_FIELDS = [
  { key: 'candidatoEmail',         label: 'Email',                type: 'email',  placeholder: 'email@exemplo.com',     autoComplete: 'email',          colSpan: 1 },
  { key: 'candidatoTelefone',      label: 'Telefone',             type: 'tel',    placeholder: '+238 9XX XXX XXX',     autoComplete: 'tel',            colSpan: 1 },
  { key: 'candidatoDocumento',     label: 'Nº BI / Passaporte',   type: 'text',   placeholder: 'Número do documento',  autoComplete: 'off',            colSpan: 1 },
  { key: 'candidatoNif',           label: 'NIF',                  type: 'text',   placeholder: 'Número de contribuinte', autoComplete: 'off',          colSpan: 1 },
  { key: 'candidatoDataNascimento',label: 'Data de nascimento',   type: 'date',   placeholder: '',                     autoComplete: 'bday',           colSpan: 1 },
  { key: 'candidatoSexo',          label: 'Sexo',                 type: 'select', placeholder: '',                     autoComplete: 'sex',            colSpan: 1 },
  { key: 'candidatoMorada',        label: 'Morada',               type: 'text',   placeholder: 'Rua, número, localidade', autoComplete: 'street-address', colSpan: 2 },
]

interface FormState {
  candidatoNome: string; candidatoEmail: string; candidatoTelefone: string
  candidatoDocumento: string; candidatoNif: string; candidatoDataNascimento: string
  candidatoMorada: string; candidatoSexo: string
  categoria: string; metodoPagamentoEscolhido: string; numParcelas: string
  zonaId: string; sucursalId: string
}

const INITIAL_FORM: FormState = {
  candidatoNome: '', candidatoEmail: '', candidatoTelefone: '',
  candidatoDocumento: '', candidatoNif: '', candidatoDataNascimento: '',
  candidatoMorada: '', candidatoSexo: '',
  categoria: '', metodoPagamentoEscolhido: '', numParcelas: '1',
  zonaId: '', sucursalId: '',
}

// ── helpers de input ─────────────────────────────────────────────────────────

function FieldIcon({ campo }: { campo: string }) {
  const icons: Record<string, React.ComponentType<{ className?: string }>> = {
    candidatoEmail: Mail, candidatoTelefone: Phone, candidatoDocumento: Hash,
    candidatoNif: Hash, candidatoDataNascimento: Calendar, candidatoMorada: Home,
    candidatoSexo: User,
  }
  const Icon = icons[campo] ?? User
  return <Icon className="w-4 h-4" />
}

function FloatInput({
  label, required, icon, type = 'text', value, onChange, placeholder, autoComplete, primary,
}: {
  label: string; required?: boolean; icon?: React.ReactNode; type?: string
  value: string; onChange: (v: string) => void; placeholder?: string
  autoComplete?: string; primary: string
}) {
  const [focused, setFocused] = useState(false)
  const active = focused || !!value
  return (
    <div className="relative">
      <div className={`flex items-center gap-2.5 border-b-2 pb-1.5 transition-colors duration-200 ${focused ? '' : 'border-gray-200'}`}
        style={{ borderBottomColor: focused ? primary : undefined }}>
        {icon && <span className="shrink-0" style={{ color: focused ? primary : '#9ca3af' }}>{icon}</span>}
        <div className="relative flex-1">
          <label className={`absolute left-0 transition-all duration-200 pointer-events-none select-none ${active ? '-top-4 text-xs font-semibold' : 'top-0 text-sm text-gray-400'}`}
            style={{ color: active ? primary : undefined }}>
            {label}{required ? ' *' : ''}
          </label>
          {type === 'select' ? null : (
            <input type={type} value={value} autoComplete={autoComplete}
              placeholder={focused ? placeholder : ''}
              onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
              onChange={e => onChange(e.target.value)}
              className="w-full pt-3 pb-0.5 text-sm text-gray-900 bg-transparent focus:outline-none" />
          )}
        </div>
      </div>
    </div>
  )
}

function FloatSelect({
  label, required, icon, value, onChange, children, primary,
}: {
  label: string; required?: boolean; icon?: React.ReactNode
  value: string; onChange: (v: string) => void
  children: React.ReactNode; primary: string
}) {
  const [focused, setFocused] = useState(false)
  const active = focused || !!value
  return (
    <div className="relative">
      <div className={`flex items-center gap-2.5 border-b-2 pb-1.5 transition-colors duration-200`}
        style={{ borderBottomColor: focused ? primary : '#e5e7eb' }}>
        {icon && <span className="shrink-0" style={{ color: focused ? primary : '#9ca3af' }}>{icon}</span>}
        <div className="relative flex-1">
          <label className={`absolute left-0 transition-all duration-200 pointer-events-none select-none z-10 ${active ? '-top-4 text-xs font-semibold' : 'top-0 text-sm text-gray-400'}`}
            style={{ color: active ? primary : undefined }}>
            {label}{required ? ' *' : ''}
          </label>
          <select value={value} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
            onChange={e => onChange(e.target.value)}
            className="w-full pt-3 pb-0.5 text-sm text-gray-900 bg-transparent focus:outline-none appearance-none cursor-pointer pr-4">
            {children}
          </select>
          <ChevronDown className="w-3.5 h-3.5 absolute right-0 top-3.5 text-gray-400 pointer-events-none" />
        </div>
      </div>
    </div>
  )
}

// ── Formulário principal ───────────────────────────────────────────────────────

interface DocState { url: string; nomeOriginal: string; mimeType: string }

function InscricaoForm({ school, colors, onSuccess, categoriasMap = {} }: { school: PortalInfo; colors: ReturnType<typeof getColors>; onSuccess: (num: string) => void; categoriasMap?: Record<string, string> }) {
  const [step, setStep]     = useState(1)
  const [form, setForm]     = useState<FormState>(INITIAL_FORM)
  const [sending, setSending] = useState(false)
  const [error, setError]   = useState<string | null>(null)
  const [docs, setDocs]     = useState<Record<string, DocState | null>>({})
  const [docsUploading, setDocsUploading] = useState<Record<string, boolean>>({})

  const visibleFields = school.camposLanding.length > 0 ? school.camposLanding : school.camposObrigatorios
  const isRequired    = (campo: string) => visibleFields.includes(campo)
  const fieldsToShow  = FORM_FIELDS.filter(f => visibleFields.includes(f.key))

  const temZonas = school.zonas.length > 0
  const sucursaisVisiveis = temZonas && form.zonaId
    ? school.sucursais.filter(d => d.zonaId === form.zonaId)
    : school.sucursais

  const categoriaAtual  = form.categoria || school.categorias[0] || ''
  const valorCategoria  = school.valoresPorCategoria?.[categoriaAtual] ?? 0

  const metodos = [
    ...(school.metodosAceites.includes('vinti4')       ? [{ key: 'vinti4',        label: 'Vinti4',        icon: CreditCard }] : []),
    ...(school.metodosAceites.includes('transferencia') ? [{ key: 'transferencia', label: 'Transferência', icon: Banknote }]   : []),
    ...(school.metodosAceites.includes('cash')          ? [{ key: 'cash',          label: 'Numerário',     icon: Coins }]      : []),
  ]

  function set(k: keyof FormState, v: string) { setForm(f => ({ ...f, [k]: v })) }
  function setZona(zId: string) { setForm(f => ({ ...f, zonaId: zId, sucursalId: '' })) }
  function setCategoria(cat: string) { setForm(f => ({ ...f, categoria: cat, sucursalId: '' })) }

  async function uploadDoc(tipo: string, file: File) {
    setDocsUploading(prev => ({ ...prev, [tipo]: true }))
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.post(`/upload/documento-publico/${school.slug}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setDocs(prev => ({ ...prev, [tipo]: { url: res.data.url, nomeOriginal: res.data.nomeOriginal, mimeType: res.data.mimeType } }))
    } catch {
      setError(`Erro ao enviar "${DOCUMENTOS_LABELS[tipo] ?? tipo}". Tente novamente.`)
    } finally {
      setDocsUploading(prev => ({ ...prev, [tipo]: false }))
    }
  }

  function validateStep1(): boolean {
    if (!form.candidatoNome.trim()) { setError('O nome é obrigatório.'); return false }
    const labels: Record<string, string> = {
      candidatoEmail: 'email', candidatoTelefone: 'telefone',
      candidatoDocumento: 'número de identificação', candidatoNif: 'NIF',
      candidatoDataNascimento: 'data de nascimento', candidatoMorada: 'morada', candidatoSexo: 'sexo',
    }
    for (const campo of visibleFields) {
      if (!form[campo as keyof FormState]?.trim()) {
        setError(`O campo "${labels[campo] ?? campo}" é obrigatório.`); return false
      }
    }
    return true
  }

  function goNext() {
    if (validateStep1()) { setError(null); setStep(2) }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (step === 1) { goNext(); return }
    setError(null)
    if (!categoriaAtual) { setError('Seleccione uma categoria.'); return }
    // Validar documentos obrigatórios
    const docsRequired = school.documentosLanding ?? []
    for (const tipo of docsRequired) {
      if (!docs[tipo]) {
        setError(`O documento "${DOCUMENTOS_LABELS[tipo] ?? tipo}" é obrigatório.`); return
      }
    }
    setSending(true)
    try {
      const payload: Record<string, any> = { candidatoNome: form.candidatoNome.trim(), categoria: categoriaAtual }
      if (form.candidatoEmail)           payload.candidatoEmail = form.candidatoEmail.trim()
      if (form.candidatoTelefone)        payload.candidatoTelefone = form.candidatoTelefone.trim()
      if (form.candidatoDocumento)       payload.candidatoDocumento = form.candidatoDocumento.trim()
      if (form.candidatoNif)             payload.candidatoNif = form.candidatoNif.trim()
      if (form.candidatoDataNascimento)  payload.candidatoDataNascimento = form.candidatoDataNascimento
      if (form.candidatoMorada)          payload.candidatoMorada = form.candidatoMorada.trim()
      if (form.candidatoSexo)            payload.candidatoSexo = form.candidatoSexo
      if (form.metodoPagamentoEscolhido) payload.metodoPagamentoEscolhido = form.metodoPagamentoEscolhido
      if (form.sucursalId)              payload.sucursalId = form.sucursalId
      if (school.maxParcelas > 1 && form.numParcelas) payload.numParcelas = Number(form.numParcelas)
      const docsList = Object.entries(docs)
        .filter(([, v]) => v !== null)
        .map(([tipo, v]) => ({ tipo, url: v!.url, nomeOriginal: v!.nomeOriginal, mimeType: v!.mimeType }))
      if (docsList.length) payload.documentos = docsList
      const res = await api.post('/inscricoes/publica', payload)
      onSuccess(res.data.numero)
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível enviar a inscrição. Tente novamente.').mensagem)
    } finally { setSending(false) }
  }

  const p = colors.primary

  return (
    <form onSubmit={submit} noValidate>

      {/* ── Progress indicator ── */}
      <div className="flex items-center gap-0 mb-8">
        {[{ n: 1, label: 'Dados Pessoais' }, { n: 2, label: 'Curso & Pagamento' }].map((s, i) => (
          <div key={s.n} className="flex items-center flex-1">
            <button type="button" onClick={() => s.n < step && setStep(s.n)}
              className={`flex items-center gap-2 text-sm font-semibold transition-colors ${s.n <= step ? '' : 'opacity-40 cursor-default'}`}
              style={{ color: s.n <= step ? p : '#6b7280' }}>
              <span className="flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0 transition-all"
                style={{
                  background: s.n < step ? p : s.n === step ? `${p}18` : '#f3f4f6',
                  color: s.n < step ? '#fff' : s.n === step ? p : '#9ca3af',
                  border: s.n === step ? `2px solid ${p}` : '2px solid transparent',
                }}>
                {s.n < step ? <CheckCircle className="w-3.5 h-3.5" /> : s.n}
              </span>
              <span className="hidden sm:block">{s.label}</span>
            </button>
            {i === 0 && (
              <div className="flex-1 mx-3 h-px" style={{ background: step > 1 ? p : '#e5e7eb' }} />
            )}
          </div>
        ))}
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="flex items-start gap-2.5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-6">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {/* ═══════════════════════════════════ STEP 1 ══════════════════════════════ */}
      {step === 1 && (
        <div className="space-y-7">
          {/* Nome */}
          <FloatInput label="Nome completo" required icon={<User className="w-4 h-4" />}
            value={form.candidatoNome} onChange={v => set('candidatoNome', v)}
            placeholder="Nome e apelido" autoComplete="name" primary={p} />

          {/* Outros campos configurados */}
          <div className="grid sm:grid-cols-2 gap-7">
            {fieldsToShow.map(f => (
              <div key={f.key} className={f.colSpan === 2 ? 'sm:col-span-2' : ''}>
                {f.type === 'select' ? (
                  <FloatSelect label={f.label} required={isRequired(f.key)}
                    icon={<FieldIcon campo={f.key} />}
                    value={form[f.key as keyof FormState] ?? ''}
                    onChange={v => set(f.key as keyof FormState, v)} primary={p}>
                    <option value="">Seleccionar</option>
                    <option value="M">Masculino</option>
                    <option value="F">Feminino</option>
                  </FloatSelect>
                ) : (
                  <FloatInput label={f.label} required={isRequired(f.key)}
                    icon={<FieldIcon campo={f.key} />}
                    type={f.type} value={form[f.key as keyof FormState] ?? ''}
                    onChange={v => set(f.key as keyof FormState, v)}
                    placeholder={f.placeholder} autoComplete={f.autoComplete} primary={p} />
                )}
              </div>
            ))}
          </div>

          <button type="button" onClick={goNext}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-sm text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
            style={{ background: `linear-gradient(135deg, ${p}, ${p}cc)` }}>
            Continuar <ChevronRight className="w-4 h-4" />
          </button>
          <p className="text-xs text-center text-gray-400">Gratuito e sem compromisso. Os seus dados são confidenciais.</p>
        </div>
      )}

      {/* ═══════════════════════════════════ STEP 2 ══════════════════════════════ */}
      {step === 2 && (
        <div className="space-y-7">

          {/* Categoria */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Categoria pretendida *</p>
            {school.categorias.length <= 6 ? (
              <div className={`grid gap-3 ${school.categorias.length === 1 ? 'grid-cols-1' : school.categorias.length <= 4 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                {school.categorias.map(cat => {
                  const active = (form.categoria || school.categorias[0]) === cat
                  const valor  = school.valoresPorCategoria?.[cat]
                  return (
                    <button key={cat} type="button" onClick={() => setCategoria(cat)}
                      className="relative flex flex-col items-center justify-center gap-1 py-4 px-3 rounded-xl border-2 transition-all duration-200 text-center hover:-translate-y-0.5"
                      style={{
                        borderColor: active ? p : '#e5e7eb',
                        background:  active ? `${p}0d` : '#fff',
                        boxShadow:   active ? `0 0 0 3px ${p}22, 0 4px 12px ${p}22` : undefined,
                      }}>
                      {active && (
                        <span className="absolute top-2 right-2 w-4 h-4 rounded-full flex items-center justify-center"
                          style={{ background: p }}>
                          <CheckCircle className="w-2.5 h-2.5 text-white" />
                        </span>
                      )}
                      <Car className="w-6 h-6 mb-0.5" style={{ color: active ? p : '#9ca3af' }} />
                      <span className="text-lg font-black" style={{ color: active ? p : '#374151' }}>Cat. {cat}</span>
                      {getNomeCategoria(cat, school.nomesCategorias, categoriasMap) && (
                        <span className="text-xs text-gray-400 leading-tight">{getNomeCategoria(cat, school.nomesCategorias, categoriasMap)}</span>
                      )}
                      {valor ? (
                        <span className="text-xs font-bold mt-1" style={{ color: active ? p : '#6b7280' }}>{fmtCVE(valor, school.moeda)}</span>
                      ) : (
                        <span className="text-xs text-gray-300 mt-1">Sob consulta</span>
                      )}
                    </button>
                  )
                })}
              </div>
            ) : (
              <FloatSelect label="Categoria" required icon={<Car className="w-4 h-4" />}
                value={form.categoria} onChange={setCategoria} primary={p}>
                <option value="">Seleccionar</option>
                {school.categorias.map(cat => (
                  <option key={cat} value={cat}>
                    Categoria {cat}{getNomeCategoria(cat, school.nomesCategorias, categoriasMap) ? ` — ${getNomeCategoria(cat, school.nomesCategorias, categoriasMap)}` : ''}
                    {school.valoresPorCategoria[cat] ? ` (${fmtCVE(school.valoresPorCategoria[cat], school.moeda)})` : ''}
                  </option>
                ))}
              </FloatSelect>
            )}
          </div>

          {/* Zona + Sucursal */}
          {(temZonas || school.sucursais.length > 0) && (
            <div className="space-y-7">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 -mb-4">Localização</p>
              <div className={`grid gap-7 ${temZonas && school.sucursais.length > 0 ? 'sm:grid-cols-2' : ''}`}>
                {temZonas && (
                  <FloatSelect label="Zona" icon={<MapPin className="w-4 h-4" />}
                    value={form.zonaId} onChange={setZona} primary={p}>
                    <option value="">— Todas —</option>
                    {school.zonas.map(z => <option key={z.id} value={z.id}>{z.nome}</option>)}
                  </FloatSelect>
                )}
                {school.sucursais.length > 0 && (
                  <FloatSelect label="Sucursal / Posto" icon={<GitBranch className="w-4 h-4" />}
                    value={form.sucursalId} onChange={v => set('sucursalId', v)} primary={p}>
                    <option value="">— Sem preferência —</option>
                    {sucursaisVisiveis.map(d => (
                      <option key={d.id} value={d.id}>{d.nome}{d.morada ? ` — ${d.morada}` : ''}</option>
                    ))}
                  </FloatSelect>
                )}
              </div>
            </div>
          )}

          {/* Método de pagamento */}
          {metodos.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Método de pagamento</p>
              <div className="flex gap-2.5">
                {metodos.map(m => {
                  const active = form.metodoPagamentoEscolhido === m.key
                  const Icon = m.icon
                  return (
                    <button key={m.key} type="button" onClick={() => set('metodoPagamentoEscolhido', m.key)}
                      className="flex flex-1 flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 text-xs font-semibold transition-all duration-200 hover:-translate-y-0.5"
                      style={{
                        borderColor: active ? p : '#e5e7eb',
                        background:  active ? `${p}0d` : '#fff',
                        color:       active ? p : '#9ca3af',
                        boxShadow:   active ? `0 0 0 3px ${p}22` : undefined,
                      }}>
                      <Icon className="w-5 h-5" /> {m.label}
                    </button>
                  )
                })}
              </div>
              {/* Dados de transferência */}
              {form.metodoPagamentoEscolhido === 'transferencia' && school.transferenciaIban && (
                <div className="mt-3 rounded-xl p-4 text-xs space-y-1.5" style={{ background: `${p}08`, border: `1px solid ${p}22` }}>
                  <p className="font-semibold mb-2" style={{ color: p }}>Dados para transferência</p>
                  <p className="text-gray-600">IBAN: <span className="font-mono font-bold text-gray-800">{school.transferenciaIban}</span></p>
                  {school.transferenciaBanco   && <p className="text-gray-600">Banco: <span className="text-gray-800">{school.transferenciaBanco}</span></p>}
                  {school.transferenciaTitular && <p className="text-gray-600">Titular: <span className="text-gray-800">{school.transferenciaTitular}</span></p>}
                </div>
              )}
            </div>
          )}

          {/* Número de parcelas */}
          {school.maxParcelas > 1 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Número de prestações</p>
              <div className="flex gap-2 flex-wrap">
                {Array.from({ length: school.maxParcelas }, (_, i) => i + 1).map(n => {
                  const active = Number(form.numParcelas) === n
                  return (
                    <button key={n} type="button" onClick={() => set('numParcelas', String(n))}
                      className="px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all duration-200"
                      style={{
                        borderColor: active ? p : '#e5e7eb',
                        background:  active ? `${p}0d` : '#fff',
                        color:       active ? p : '#6b7280',
                      }}>
                      {n === 1 ? 'Único' : `${n}×`}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Documentos obrigatórios */}
          {(school.documentosLanding ?? []).length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Documentos necessários</p>
              <div className="space-y-3">
                {(school.documentosLanding ?? []).map(tipo => {
                  const doc = docs[tipo]
                  const uploading = docsUploading[tipo]
                  return (
                    <label key={tipo} className="flex items-center justify-between gap-4 p-3 rounded-xl border-2 cursor-pointer transition-all"
                      style={{
                        borderColor: doc ? p : '#e5e7eb',
                        background: doc ? `${p}08` : '#fafafa',
                      }}>
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 shrink-0" style={{ color: doc ? p : '#9ca3af' }} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-700 truncate">{DOCUMENTOS_LABELS[tipo] ?? tipo}</p>
                          {doc
                            ? <p className="text-xs truncate" style={{ color: p }}>{doc.nomeOriginal}</p>
                            : <p className="text-xs text-gray-400">JPG, PNG ou PDF — máx. 10 MB</p>
                          }
                        </div>
                      </div>
                      {uploading
                        ? <Loader2 className="w-5 h-5 animate-spin shrink-0 text-gray-400" />
                        : doc
                          ? <CheckCircle className="w-5 h-5 shrink-0" style={{ color: p }} />
                          : <span className="text-xs font-semibold shrink-0 px-2.5 py-1 rounded-lg text-white" style={{ background: p }}>Carregar</span>
                      }
                      <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.pdf"
                        onChange={e => { const f = e.target.files?.[0]; if (f) uploadDoc(tipo, f) }} />
                    </label>
                  )
                })}
              </div>
            </div>
          )}

          {/* Resumo */}
          {valorCategoria > 0 && (
            <div className="rounded-xl p-4 flex items-center justify-between"
              style={{ background: `${p}0a`, border: `1px solid ${p}20` }}>
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Categoria {categoriaAtual}</p>
                <p className="text-xl font-black" style={{ color: p }}>{fmtCVE(valorCategoria, school.moeda)}</p>
                {Number(form.numParcelas) > 1 && (
                  <p className="text-xs text-gray-400">≈ {fmtCVE(Math.ceil(valorCategoria / Number(form.numParcelas)), school.moeda)} / prestação</p>
                )}
              </div>
              <BadgeCheck className="w-9 h-9 opacity-20" style={{ color: p }} />
            </div>
          )}

          {/* Botões */}
          <div className="flex gap-3">
            <button type="button" onClick={() => { setStep(1); setError(null) }}
              className="flex items-center gap-1.5 px-4 py-3 rounded-xl border-2 border-gray-200 text-sm font-medium text-gray-500 hover:border-gray-300 transition-colors">
              <ChevronLeft className="w-4 h-4" /> Voltar
            </button>
            <button type="submit" disabled={sending}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-sm text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-60 disabled:transform-none"
              style={{ background: `linear-gradient(135deg, ${p}, ${p}cc)` }}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {sending ? 'A enviar...' : 'Enviar pré-inscrição'}
            </button>
          </div>
          <p className="text-xs text-center text-gray-400">Gratuito e sem compromisso. Receberá confirmação por email ou WhatsApp.</p>
        </div>
      )}
    </form>
  )
}

// ── Componentes de secções ────────────────────────────────────────────────────

interface SectionProps {
  school: PortalInfo
  cfg: LandingConfig | null
  colors: ReturnType<typeof getColors>
  escolaSlug: string
  scrollToForm: () => void
  mostrarFormulario: boolean
  categoriasMap: Record<string, string>
  secCfg?: LandingSectionConfig
  formRef?: React.RefObject<HTMLDivElement | null>
}

function CategoriesSection({ school, colors, scrollToForm, mostrarFormulario, categoriasMap, secCfg }: SectionProps) {
  if (!school.categorias.length) return null
  const hasCustomBg = secCfg?.background && secCfg.background.type !== 'transparent'
  const hasPadding = !!secCfg?.padding
  const accent = secCfg?.accentColor ?? colors.primary
  const hColor = secCfg?.headingColor ?? '#111827'
  const card = secCfg?.cardStyle
  const sectionTitle = secCfg?.texts?.title ?? 'Categorias disponíveis'
  const sectionSubtitle = secCfg?.texts?.subtitle ?? 'Escolha a categoria que se adapta às suas necessidades'
  const ctaText = secCfg?.texts?.ctaText ?? 'Inscrever nesta categoria'
  const cardBg = card?.bgColor ?? '#ffffff'
  const cardBorder = card?.borderColor ?? '#e5e7eb'
  const cardRadius = card?.borderRadius ?? 16
  const cardTextColor = card?.textColor ?? undefined
  const cardShadow = card?.shadow === 'none' ? 'none' : card?.shadow === 'lg' ? '0 10px 20px rgba(0,0,0,0.1)' : card?.shadow === 'md' ? '0 4px 8px rgba(0,0,0,0.08)' : card?.shadow === 'sm' ? '0 1px 3px rgba(0,0,0,0.06)' : undefined
  return (
    <SectionWrapper sec={secCfg}>
      <section className={`${hasPadding ? '' : 'py-16 md:py-20 '}px-6${hasCustomBg ? '' : ' bg-gray-50'}`}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: hColor }}>{sectionTitle}</h2>
            <p className="max-w-lg mx-auto" style={{ color: secCfg?.bodyColor ?? '#6b7280' }}>{sectionSubtitle}</p>
          </div>
          <div className={`grid gap-5 ${school.categorias.length === 1 ? 'max-w-xs mx-auto' : school.categorias.length === 2 ? 'sm:grid-cols-2 max-w-lg mx-auto' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
            {school.categorias.map(cat => (
              <div key={cat} className="border flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1"
                style={{ backgroundColor: cardBg, borderColor: cardBorder, borderRadius: cardRadius, padding: '24px', boxShadow: cardShadow, color: cardTextColor }}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${accent}15` }}>
                  <Car className="w-8 h-8" style={{ color: accent }} />
                </div>
                <div className="text-3xl font-black mb-1" style={{ color: accent }}>Cat. {cat}</div>
                {getNomeCategoria(cat, school.nomesCategorias, categoriasMap) && <p className="text-sm mb-3" style={{ color: cardTextColor ? `${cardTextColor}99` : '#6b7280' }}>{getNomeCategoria(cat, school.nomesCategorias, categoriasMap)}</p>}
                {school.valoresPorCategoria[cat] ? (
                  <div className="mt-auto pt-4 border-t w-full" style={{ borderColor: cardBorder }}>
                    <span className="text-xl font-bold" style={{ color: cardTextColor ?? '#111827' }}>{fmtCVE(school.valoresPorCategoria[cat], school.moeda)}</span>
                    {school.maxParcelas > 1 && <p className="text-xs mt-1" style={{ color: cardTextColor ? `${cardTextColor}80` : '#9ca3af' }}>até {school.maxParcelas} prestações</p>}
                  </div>
                ) : (
                  <div className="mt-auto pt-4 border-t w-full" style={{ borderColor: cardBorder }}>
                    <span className="text-sm italic" style={{ color: cardTextColor ? `${cardTextColor}60` : '#9ca3af' }}>Preço sob consulta</span>
                  </div>
                )}
                {mostrarFormulario && (
                  <button onClick={scrollToForm}
                    className="mt-4 w-full font-semibold text-sm py-2.5 rounded-xl transition-colors"
                    style={{ backgroundColor: `${accent}12`, color: accent }}>
                    {ctaText}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </SectionWrapper>
  )
}

function HowItWorksSection({ cfg, colors, secCfg }: SectionProps) {
  const defaultSteps = [
    { titulo: 'Pré-inscrição', descricao: 'Preencha o formulário com os seus dados. É rápido e gratuito.' },
    { titulo: 'Confirmação', descricao: 'A escola analisa a sua inscrição e entra em contacto para confirmar.' },
    { titulo: 'Formação', descricao: 'Após confirmação, inicia a formação teórica e prática na nossa escola.' },
  ]
  const steps = (cfg?.howItWorksSteps && cfg.howItWorksSteps.length > 0) ? cfg.howItWorksSteps : defaultSteps
  const hasCustomBg = secCfg?.background && secCfg.background.type !== 'transparent'
  const hasPadding = !!secCfg?.padding
  const accent = secCfg?.accentColor ?? colors.primary
  const hColor = secCfg?.headingColor ?? '#111827'
  const card = secCfg?.cardStyle
  const sectionTitle = secCfg?.texts?.title ?? 'Como funciona'
  const sectionSubtitle = secCfg?.texts?.subtitle ?? `O processo de inscrição em ${steps.length} passos simples`
  const cardBg = card?.bgColor
  const cardRadius = card?.borderRadius ?? 16
  const cardShadow = card?.shadow === 'lg' ? '0 10px 20px rgba(0,0,0,0.1)' : card?.shadow === 'md' ? '0 4px 8px rgba(0,0,0,0.08)' : card?.shadow === 'sm' ? '0 1px 3px rgba(0,0,0,0.06)' : card?.shadow === 'none' ? 'none' : undefined
  return (
    <SectionWrapper sec={secCfg}>
      <section className={`${hasPadding ? '' : 'py-16 md:py-20 '}px-6${hasCustomBg ? '' : ' bg-white'}`}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: hColor }}>{sectionTitle}</h2>
            <p style={{ color: secCfg?.bodyColor ?? '#6b7280' }}>{sectionSubtitle}</p>
          </div>
          <div className={`grid gap-8 ${steps.length <= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
            {steps.map((s, i) => (
              <div key={i} className="text-center group"
                style={cardBg ? { backgroundColor: cardBg, borderRadius: cardRadius, padding: '24px', boxShadow: cardShadow } : {}}>
                <div className="w-14 h-14 text-white rounded-2xl flex items-center justify-center text-xl font-black mx-auto mb-4 shadow-md group-hover:scale-110 transition-transform" style={{ backgroundColor: accent }}>{i + 1}</div>
                <h3 className="font-bold mb-2 text-lg" style={{ color: secCfg?.headingColor ?? '#111827' }}>{s.titulo}</h3>
                <p className="text-sm leading-relaxed" style={{ color: secCfg?.bodyColor ?? '#6b7280' }}>{s.descricao}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SectionWrapper>
  )
}

function AboutSection({ cfg, secCfg }: SectionProps) {
  if (!cfg?.aboutTitle && !cfg?.aboutText) return null
  const hasCustomBg = secCfg?.background && secCfg.background.type !== 'transparent'
  const hasPadding = !!secCfg?.padding
  return (
    <SectionWrapper sec={secCfg}>
      <section className={`${hasPadding ? '' : 'py-16 md:py-20 '}px-6${hasCustomBg ? '' : ' bg-gray-50'}`}>
        <div className="max-w-5xl mx-auto">
          <div className={`${cfg.aboutImageUrl ? 'grid md:grid-cols-2 gap-10 items-center' : 'max-w-3xl mx-auto text-center'}`}>
            <div>
              {cfg.aboutTitle && <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: secCfg?.headingColor ?? '#111827' }}>{cfg.aboutTitle}</h2>}
              {cfg.aboutText && <p className="leading-relaxed whitespace-pre-line" style={{ color: secCfg?.bodyColor ?? '#4b5563' }}>{cfg.aboutText}</p>}
            </div>
            {cfg.aboutImageUrl && (
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img src={cfg.aboutImageUrl} alt={cfg.aboutTitle || ''} className="w-full h-64 md:h-80 object-cover" />
              </div>
            )}
          </div>
        </div>
      </section>
    </SectionWrapper>
  )
}

function FormSection({ school, colors, mostrarFormulario, categoriasMap, secCfg }: SectionProps) {
  const [success, setSuccess] = useState<string | null>(null)
  if (!mostrarFormulario) return null

  const p = secCfg?.accentColor ?? colors.primary
  const s = colors.secondary
  const hColor = secCfg?.headingColor ?? '#111827'
  const hasCustomBg = secCfg?.background && secCfg.background.type !== 'transparent'
  const hasPadding = !!secCfg?.padding

  const sectionTitle = secCfg?.texts?.title
  const sectionSubtitle = secCfg?.texts?.subtitle
  const badgeText = secCfg?.texts?.badge ?? 'Pré-inscrição gratuita'
  const defaultBenefits = [
    { icon: Shield,  title: secCfg?.texts?.benefit1 ?? 'Processo seguro',    desc: secCfg?.texts?.benefit1Desc ?? 'Os seus dados são tratados com total confidencialidade.' },
    { icon: Clock,   title: secCfg?.texts?.benefit2 ?? 'Resposta rápida',     desc: secCfg?.texts?.benefit2Desc ?? 'Confirmação em até 24 horas por email ou WhatsApp.' },
    { icon: Trophy,  title: secCfg?.texts?.benefit3 ?? 'Aprovação garantida', desc: secCfg?.texts?.benefit3Desc ?? 'Formação de qualidade com instrutores certificados.' },
  ]

  const defaultBg: React.CSSProperties = hasCustomBg ? {} : { background: `linear-gradient(160deg, ${s}f5 0%, ${p}12 100%)` }

  return (
    <SectionWrapper sec={secCfg}>
      <section className={`scroll-mt-20${hasPadding ? '' : ''}`} id="inscricao" style={defaultBg}>
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
          <div className="grid lg:grid-cols-[1fr_500px] gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-6"
                style={{ background: `${p}15`, color: p }}>
                <GraduationCap className="w-3.5 h-3.5" /> {badgeText}
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black leading-tight mb-4" style={{ color: hColor }}>
                {sectionTitle ?? (<>Comece a sua <span style={{ color: p }}>jornada</span><br />hoje mesmo</>)}
              </h2>
              <p className="text-lg leading-relaxed mb-10" style={{ color: secCfg?.bodyColor ?? '#4b5563' }}>
                {sectionSubtitle ?? 'Preencha o formulário em menos de 2 minutos e garanta a sua vaga. É gratuito e sem qualquer compromisso.'}
              </p>
              <div className="space-y-5">
                {defaultBenefits.map((b, i) => {
                  const Icon = b.icon
                  return (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
                        style={{ background: `${p}15` }}>
                        <Icon className="w-5 h-5" style={{ color: p }} />
                      </div>
                      <div>
                        <p className="font-semibold" style={{ color: hColor }}>{b.title}</p>
                        <p className="text-sm mt-0.5" style={{ color: secCfg?.bodyColor ?? '#6b7280' }}>{b.desc}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
              {school.phone && (
                <div className="mt-10 flex items-center gap-3 text-sm" style={{ color: secCfg?.bodyColor ?? '#6b7280' }}>
                  <Phone className="w-4 h-4 shrink-0" style={{ color: p }} />
                  Prefere falar? Ligue-nos: <a href={`tel:${school.phone}`} className="font-semibold hover:underline" style={{ color: p }}>{school.phone}</a>
                </div>
              )}
            </div>
            <div>
              {success ? (
                <div className="bg-white rounded-3xl shadow-2xl p-8 text-center" style={{ boxShadow: `0 25px 60px ${p}20` }}>
                  <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: `${p}12` }}>
                    <CheckCircle className="w-10 h-10" style={{ color: p }} />
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-2">Inscrição recebida!</h3>
                  <p className="text-gray-500 mb-5">Guarde o número da sua pré-inscrição:</p>
                  <div className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl mb-6 font-mono text-xl font-black" style={{ background: `${p}10`, color: p, border: `2px solid ${p}30` }}>{success}</div>
                  <div className="space-y-3 text-sm text-gray-500 mb-8">
                    <div className="flex items-center gap-2 justify-center"><CheckCircle className="w-4 h-4 text-green-500 shrink-0" /><span>Receberá confirmação por email ou WhatsApp</span></div>
                    <div className="flex items-center gap-2 justify-center"><CheckCircle className="w-4 h-4 text-green-500 shrink-0" /><span>A escola entrará em contacto em breve</span></div>
                  </div>
                  <button onClick={() => setSuccess(null)} className="text-sm font-semibold hover:underline transition-colors" style={{ color: p }}>Fazer outra inscrição →</button>
                </div>
              ) : (
                <div className="bg-white rounded-3xl shadow-2xl p-8" style={{ boxShadow: `0 25px 60px ${p}18` }}>
                  <div className="mb-6">
                    <h3 className="text-xl font-black text-gray-900">Formulário de inscrição</h3>
                    <p className="text-sm text-gray-400 mt-1">{school.name}</p>
                  </div>
                  <InscricaoForm school={school} colors={{ ...colors, primary: p }} onSuccess={setSuccess} categoriasMap={categoriasMap} />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </SectionWrapper>
  )
}

function TestimonialsSection({ cfg, colors, secCfg }: SectionProps) {
  if (!cfg?.testimonials?.length) return null
  const hasCustomBg = secCfg?.background && secCfg.background.type !== 'transparent'
  const hasPadding = !!secCfg?.padding
  const hColor = secCfg?.headingColor ?? '#111827'
  const accent = secCfg?.accentColor ?? colors.primary
  const card = secCfg?.cardStyle
  const sectionTitle = secCfg?.texts?.title ?? 'O que dizem os nossos alunos'
  const sectionSubtitle = secCfg?.texts?.subtitle ?? 'Testemunhos de quem já passou por aqui'
  const cardBg = card?.bgColor ?? '#f9fafb'
  const cardBorder = card?.borderColor ?? '#f3f4f6'
  const cardRadius = card?.borderRadius ?? 16
  const cardTextColor = card?.textColor ?? undefined
  const cardShadow = card?.shadow === 'none' ? 'none' : card?.shadow === 'lg' ? '0 10px 20px rgba(0,0,0,0.1)' : card?.shadow === 'md' ? '0 4px 8px rgba(0,0,0,0.08)' : card?.shadow === 'sm' ? '0 1px 3px rgba(0,0,0,0.06)' : undefined
  return (
    <SectionWrapper sec={secCfg}>
      <section className={`${hasPadding ? '' : 'py-16 md:py-20 '}px-6${hasCustomBg ? '' : ' bg-white'}`}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: hColor }}>{sectionTitle}</h2>
            <p style={{ color: secCfg?.bodyColor ?? '#6b7280' }}>{sectionSubtitle}</p>
          </div>
          <div className={`grid gap-6 ${cfg.testimonials.length === 1 ? 'max-w-md mx-auto' : cfg.testimonials.length === 2 ? 'sm:grid-cols-2 max-w-3xl mx-auto' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
            {cfg.testimonials.map((t, i) => (
              <div key={i} className="border p-6 hover:shadow-md transition-shadow"
                style={{ backgroundColor: cardBg, borderColor: cardBorder, borderRadius: cardRadius, boxShadow: cardShadow, color: cardTextColor }}>
                <div className="flex items-center gap-3 mb-4">
                  {t.fotoUrl ? (
                    <img src={t.fotoUrl} alt={t.nome} className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: accent }}>
                      {t.nome.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold" style={{ color: cardTextColor ?? '#111827' }}>{t.nome}</p>
                    {t.estrelas && (
                      <div className="flex gap-0.5">
                        {Array.from({ length: 5 }).map((_, j) => (
                          <Star key={j} className={`w-3.5 h-3.5 ${j < t.estrelas! ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-sm leading-relaxed italic" style={{ color: cardTextColor ? `${cardTextColor}cc` : '#4b5563' }}>&ldquo;{t.texto}&rdquo;</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SectionWrapper>
  )
}

function PaymentSection({ school, colors, mostrarFormulario, secCfg }: SectionProps) {
  const temTransferencia = school.metodosAceites.includes('transferencia') && school.transferenciaIban
  if (!mostrarFormulario || (!temTransferencia && !school.metodosAceites.includes('cash') && !school.metodosAceites.includes('vinti4'))) return null
  const hasCustomBg = secCfg?.background && secCfg.background.type !== 'transparent'
  const hasPadding = !!secCfg?.padding
  const hColor = secCfg?.headingColor ?? '#111827'
  const accent = secCfg?.accentColor ?? colors.primary
  const card = secCfg?.cardStyle
  const sectionTitle = secCfg?.texts?.title ?? 'Formas de pagamento'
  const sectionSubtitle = secCfg?.texts?.subtitle ?? 'Escolha a forma mais conveniente para si'
  const cardBg = card?.bgColor ?? '#ffffff'
  const cardBorder = card?.borderColor ?? '#e5e7eb'
  const cardRadius = card?.borderRadius ?? 16
  const cardTextColor = card?.textColor ?? undefined
  const cardShadow = card?.shadow === 'none' ? 'none' : card?.shadow === 'lg' ? '0 10px 20px rgba(0,0,0,0.1)' : card?.shadow === 'md' ? '0 4px 8px rgba(0,0,0,0.08)' : card?.shadow === 'sm' ? '0 1px 3px rgba(0,0,0,0.06)' : undefined
  return (
    <SectionWrapper sec={secCfg}>
      <section className={`${hasPadding ? '' : 'py-16 md:py-20 '}px-6${hasCustomBg ? '' : ' bg-white'}`}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: hColor }}>{sectionTitle}</h2>
            <p style={{ color: secCfg?.bodyColor ?? '#6b7280' }}>{sectionSubtitle}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {school.metodosAceites.includes('vinti4') && (
              <div className="border p-6 hover:shadow-md transition-shadow" style={{ backgroundColor: cardBg, borderColor: cardBorder, borderRadius: cardRadius, boxShadow: cardShadow }}>
                <CreditCard className="w-8 h-8 mb-4" style={{ color: accent }} />
                <h3 className="font-bold mb-2 text-lg" style={{ color: cardTextColor ?? '#111827' }}>Cartão / Vinti4</h3>
                <p className="text-sm leading-relaxed" style={{ color: cardTextColor ? `${cardTextColor}aa` : '#6b7280' }}>Pagamento online seguro através da rede Vinti4.</p>
              </div>
            )}
            {temTransferencia && (
              <div className="border p-6 hover:shadow-md transition-shadow" style={{ backgroundColor: cardBg, borderColor: cardBorder, borderRadius: cardRadius, boxShadow: cardShadow }}>
                <Building2 className="w-8 h-8 mb-4" style={{ color: accent }} />
                <h3 className="font-bold mb-3 text-lg" style={{ color: cardTextColor ?? '#111827' }}>Transferência bancária</h3>
                <div className="space-y-1.5 text-sm">
                  {school.transferenciaBanco && <p style={{ color: cardTextColor ? `${cardTextColor}aa` : '#4b5563' }}>{school.transferenciaBanco}</p>}
                  {school.transferenciaIban && <p className="font-mono text-xs px-2.5 py-1.5 rounded-lg" style={{ background: cardBorder + '40', color: cardTextColor ?? '#1f2937' }}>{school.transferenciaIban}</p>}
                  {school.transferenciaTitular && <p className="text-xs" style={{ color: cardTextColor ? `${cardTextColor}80` : '#6b7280' }}>Titular: {school.transferenciaTitular}</p>}
                </div>
              </div>
            )}
            {school.metodosAceites.includes('cash') && (
              <div className="border p-6 hover:shadow-md transition-shadow" style={{ backgroundColor: cardBg, borderColor: cardBorder, borderRadius: cardRadius, boxShadow: cardShadow }}>
                <Banknote className="w-8 h-8 mb-4" style={{ color: accent }} />
                <h3 className="font-bold mb-2 text-lg" style={{ color: cardTextColor ?? '#111827' }}>Numerário</h3>
                <p className="text-sm leading-relaxed" style={{ color: cardTextColor ? `${cardTextColor}aa` : '#6b7280' }}>Pagamento em dinheiro directamente nas instalações da escola.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </SectionWrapper>
  )
}

function FaqSection({ cfg, colors, secCfg }: SectionProps) {
  const [openIdx, setOpenIdx] = useState<number | null>(null)
  if (!cfg?.faqItems?.length) return null
  const hasCustomBg = secCfg?.background && secCfg.background.type !== 'transparent'
  const hasPadding = !!secCfg?.padding
  const hColor = secCfg?.headingColor ?? '#111827'
  const accent = secCfg?.accentColor ?? colors.primary
  const sectionTitle = secCfg?.texts?.title ?? 'Perguntas frequentes'
  const sectionSubtitle = secCfg?.texts?.subtitle ?? 'Encontre respostas para as dúvidas mais comuns'
  return (
    <SectionWrapper sec={secCfg}>
      <section className={`${hasPadding ? '' : 'py-16 md:py-20 '}px-6${hasCustomBg ? '' : ' bg-gray-50'}`}>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: hColor }}>{sectionTitle}</h2>
            <p style={{ color: secCfg?.bodyColor ?? '#6b7280' }}>{sectionSubtitle}</p>
          </div>
          <div className="space-y-3">
            {cfg.faqItems.map((item, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <button onClick={() => setOpenIdx(openIdx === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors">
                  <span className="font-semibold pr-4" style={{ color: hColor }}>{item.pergunta}</span>
                  <ChevronDown className={`w-5 h-5 shrink-0 transition-transform duration-200 ${openIdx === i ? 'rotate-180' : ''}`} style={{ color: accent }} />
                </button>
                {openIdx === i && (
                  <div className="px-5 pb-4 text-sm leading-relaxed border-t border-gray-100 pt-3" style={{ color: secCfg?.bodyColor ?? '#4b5563' }}>
                    {item.resposta}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </SectionWrapper>
  )
}

function ContactSection({ school, colors, cfg: landingCfg, secCfg }: SectionProps) {
  const contactCfg = landingCfg?.contactConfig
  const layout = contactCfg?.layout ?? 'cards'
  const sectionTitle = secCfg?.texts?.title ?? contactCfg?.title ?? 'Contacte-nos'
  const sectionSubtitle = secCfg?.texts?.subtitle ?? contactCfg?.subtitle ?? ''

  // Build default blocks from school data if no blocks configured
  const defaultBlocks: LandingContactBlock[] = []
  if (school.address) defaultBlocks.push({ id: 'location', type: 'location', visible: true, order: 0 })
  if (school.phone)   defaultBlocks.push({ id: 'phone',    type: 'phone',    visible: true, order: 1 })
  if (school.email)   defaultBlocks.push({ id: 'email',    type: 'email',    visible: true, order: 2 })

  const rawBlocks = contactCfg?.blocks?.length ? contactCfg.blocks : defaultBlocks
  const visibleBlocks = [...rawBlocks].filter(b => b.visible).sort((a, b) => a.order - b.order)

  if (!visibleBlocks.length) return null

  // Resolve value for each block (custom value overrides school data)
  const resolveValue = (block: LandingContactBlock): string => {
    if (block.value) return block.value
    if (block.type === 'location') return school.address ?? ''
    if (block.type === 'phone')    return school.phone ?? ''
    if (block.type === 'email')    return school.email ?? ''
    return ''
  }

  // Block labels
  const DEFAULT_LABELS: Record<string, string> = {
    location: 'Localização', phone: 'Telefone', email: 'Email',
    whatsapp: 'WhatsApp', hours: 'Horário', website: 'Website',
  }

  // Block icons
  function BlockIcon({ type, className, style }: { type: string; className?: string; style?: React.CSSProperties }) {
    if (type === 'location') return <MapPin className={className} style={style} />
    if (type === 'phone')    return <Phone className={className} style={style} />
    if (type === 'email')    return <Mail className={className} style={style} />
    if (type === 'whatsapp') return <MessageSquare className={className} style={style} />
    if (type === 'hours')    return <Clock className={className} style={style} />
    return <Globe className={className} style={style} />
  }

  // Block href
  const resolveHref = (block: LandingContactBlock, value: string): string | undefined => {
    if (block.type === 'phone') return `tel:${value}`
    if (block.type === 'email') return `mailto:${value}`
    if (block.type === 'whatsapp') return `https://wa.me/${value.replace(/\D/g, '')}`
    if (block.type === 'website') return value
    if (block.type === 'location' && !block.mapEmbed) return `https://maps.google.com/?q=${encodeURIComponent(value)}`
    return undefined
  }

  // Map embed URL
  const mapQuery = visibleBlocks.find(b => b.type === 'location')
  const mapValue = mapQuery ? resolveValue(mapQuery) : ''
  const mapEmbedUrl = mapValue
    ? `https://maps.google.com/maps?q=${encodeURIComponent(mapValue)}&z=${mapQuery?.mapZoom ?? 14}&output=embed&hl=pt`
    : null

  // Block card style
  const blockStyle = (block: LandingContactBlock): React.CSSProperties => {
    if (block.bgType === 'solid' && block.bgColor) return { backgroundColor: block.bgColor }
    if (block.bgType === 'image' && block.bgImageUrl) return {
      backgroundImage: `url(${block.bgImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center',
    }
    return {}
  }

  const textLight = (block: LandingContactBlock) => block.textColor === 'light' || (!block.textColor && (block.bgType === 'solid' || block.bgType === 'image'))

  const hasCustomBg = secCfg?.background && secCfg.background.type !== 'transparent'
  const hasPadding = !!secCfg?.padding
  // Fallback bg for contact section when no custom bg is configured
  const defaultBgStyle: React.CSSProperties = hasCustomBg ? {} : { backgroundColor: '#f9fafb' }
  const sectionPad = hasPadding ? {} : { paddingTop: 64, paddingBottom: 64 }
  const titleColor = secCfg?.headingColor ?? (secCfg?.textColor === 'light' ? '#ffffff' : '#111827')
  const subtitleColor = secCfg?.bodyColor ?? (secCfg?.textColor === 'light' ? 'rgba(255,255,255,0.7)' : '#6b7280')

  // ── Render card ─────────────────────────────────────────────────────────────

  function renderCard(block: LandingContactBlock, extraCls = '') {
    const value = resolveValue(block)
    if (!value && block.type !== 'hours' && block.type !== 'website') return null
    const label = block.label ?? DEFAULT_LABELS[block.type] ?? ''
    const href = resolveHref(block, value)
    const isLight = textLight(block)
    const iconColor = block.bgType && block.bgType !== 'theme' ? (isLight ? '#ffffff' : colors.primary) : colors.primary
    const labelCls = isLight ? 'text-white/70' : 'text-gray-500'
    const valueCls = isLight ? 'text-white font-medium' : 'text-gray-800 font-medium'

    const inner = (
      <div className={`flex flex-col items-center gap-3 p-6 rounded-xl relative overflow-hidden h-full ${extraCls}`}
        style={{ backgroundColor: block.bgType === 'theme' || !block.bgType ? `${colors.primary}08` : undefined, ...blockStyle(block) }}>
        {block.bgType === 'image' && (
          <div className="absolute inset-0 bg-black/40 pointer-events-none" />
        )}
        <div className="relative z-10 w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${iconColor}20` }}>
          <BlockIcon type={block.type} className="w-6 h-6" style={{ color: iconColor }} />
        </div>
        <div className="relative z-10 text-center">
          <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${labelCls}`}>{label}</p>
          <p className={`text-sm ${valueCls} whitespace-pre-line`}>{value}</p>
        </div>
      </div>
    )

    if (href) return <a key={block.id} href={href} target={block.type === 'website' ? '_blank' : undefined} rel="noopener" className="block hover:opacity-90 transition-opacity">{inner}</a>
    return <div key={block.id}>{inner}</div>
  }

  // ── Layout: HORIZONTAL ──────────────────────────────────────────────────────

  if (layout === 'horizontal') {
    return (
      <SectionWrapper sec={secCfg}>
        <section style={{ ...defaultBgStyle, ...sectionPad, paddingLeft: 24, paddingRight: 24 }}>
          <div className="max-w-6xl mx-auto">
            {sectionTitle && <h2 className="text-2xl md:text-3xl font-bold text-center mb-2" style={{ color: titleColor }}>{sectionTitle}</h2>}
            {sectionSubtitle && <p className="text-center mb-8 text-sm" style={{ color: subtitleColor }}>{sectionSubtitle}</p>}
            <div className="flex flex-wrap justify-center gap-4">
              {visibleBlocks.map(block => {
                const value = resolveValue(block)
                if (!value) return null
                const label = block.label ?? DEFAULT_LABELS[block.type] ?? ''
                const href  = resolveHref(block, value)
                const El    = href ? 'a' : 'div'
                return (
                  <El key={block.id} {...(href ? { href, target: block.type === 'website' ? '_blank' : undefined, rel: 'noopener' } : {})}
                    className="flex items-center gap-3 px-5 py-3 rounded-xl transition-all hover:scale-105"
                    style={{ backgroundColor: `${colors.primary}10`, border: `1px solid ${colors.primary}20` }}>
                    <BlockIcon type={block.type} className="w-5 h-5 shrink-0" style={{ color: colors.primary }} />
                    <div>
                      <p className="text-xs text-gray-500">{label}</p>
                      <p className="text-sm font-semibold text-gray-800">{value}</p>
                    </div>
                  </El>
                )
              })}
            </div>
          </div>
        </section>
      </SectionWrapper>
    )
  }

  // ── Layout: MAP-HERO ────────────────────────────────────────────────────────

  if (layout === 'map-hero') {
    return (
      <SectionWrapper sec={secCfg}>
        <section style={{ ...defaultBgStyle, ...sectionPad, paddingLeft: 24, paddingRight: 24 }}>
          <div className="max-w-6xl mx-auto">
            {sectionTitle && <h2 className="text-2xl md:text-3xl font-bold text-center mb-2" style={{ color: titleColor }}>{sectionTitle}</h2>}
            {sectionSubtitle && <p className="text-center mb-8 text-sm" style={{ color: subtitleColor }}>{sectionSubtitle}</p>}
            {mapEmbedUrl && (
              <div className="w-full rounded-xl overflow-hidden mb-8" style={{ height: 360, border: `1px solid ${colors.primary}20` }}>
                <iframe src={mapEmbedUrl} width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Localização" />
              </div>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {visibleBlocks.map(b => renderCard(b))}
            </div>
          </div>
        </section>
      </SectionWrapper>
    )
  }

  // ── Layout: MAP-SIDEBAR ─────────────────────────────────────────────────────

  if (layout === 'map-sidebar') {
    return (
      <SectionWrapper sec={secCfg}>
        <section style={{ ...defaultBgStyle, ...sectionPad, paddingLeft: 24, paddingRight: 24 }}>
          <div className="max-w-6xl mx-auto">
            {sectionTitle && <h2 className="text-2xl md:text-3xl font-bold text-center mb-2" style={{ color: titleColor }}>{sectionTitle}</h2>}
            {sectionSubtitle && <p className="text-center mb-8 text-sm" style={{ color: subtitleColor }}>{sectionSubtitle}</p>}
            <div className="grid md:grid-cols-5 gap-6 items-stretch">
              {mapEmbedUrl && (
                <div className="md:col-span-3 rounded-xl overflow-hidden" style={{ minHeight: 320, border: `1px solid ${colors.primary}20` }}>
                  <iframe src={mapEmbedUrl} width="100%" height="100%" style={{ border: 0, minHeight: 320 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Localização" />
                </div>
              )}
              <div className={`${mapEmbedUrl ? 'md:col-span-2' : 'md:col-span-5'} flex flex-col gap-4`}>
                {visibleBlocks.map(b => renderCard(b, 'flex-1'))}
              </div>
            </div>
          </div>
        </section>
      </SectionWrapper>
    )
  }

  // ── Layout: CARDS (default) ─────────────────────────────────────────────────

  return (
    <SectionWrapper sec={secCfg}>
      <section style={{ ...defaultBgStyle, ...sectionPad, paddingLeft: 24, paddingRight: 24 }}>
        <div className="max-w-5xl mx-auto">
          {sectionTitle && <h2 className="text-2xl md:text-3xl font-bold text-center mb-2" style={{ color: titleColor }}>{sectionTitle}</h2>}
          {sectionSubtitle && <p className="text-center mb-8 text-sm" style={{ color: subtitleColor }}>{sectionSubtitle}</p>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {visibleBlocks.map(b => renderCard(b))}
          </div>
        </div>
      </section>
    </SectionWrapper>
  )
}

// ── Landing page completa ─────────────────────────────────────────────────────

// ── Helpers de fundo de secção ────────────────────────────────────────────────

function SectionDividerSvg({ type, color = '#ffffff', height = 60, flip = false }: { type: string; color?: string; height?: number; flip?: boolean }) {
  const paths: Record<string, string> = {
    wave:   'M0,20 C200,60 400,-20 600,20 C800,60 900,10 1000,20 L1000,60 L0,60 Z',
    angle:  'M0,60 L1000,0 L1000,60 Z',
    curve:  'M0,60 Q500,-10 1000,60 L1000,60 L0,60 Z',
    zigzag: 'M0,60 L83,0 L167,60 L250,0 L333,60 L417,0 L500,60 L583,0 L667,60 L750,0 L833,60 L917,0 L1000,60 Z',
  }
  if (type === 'none' || !paths[type]) return null
  return (
    <div style={{ overflow: 'hidden', lineHeight: 0, fontSize: 0 }}>
      <svg viewBox="0 0 1000 60" preserveAspectRatio="none"
        style={{ display: 'block', width: '100%', height, transform: flip ? 'scaleX(-1)' : undefined }}>
        <path d={paths[type]} fill={color} />
      </svg>
    </div>
  )
}

function buildSectionBgStyle(bg?: LandingBackground): React.CSSProperties {
  if (!bg || bg.type === 'transparent') return {}
  if (bg.type === 'solid') return { backgroundColor: bg.color ?? '#ffffff' }
  if (bg.type === 'gradient' && bg.gradient) {
    return { background: `linear-gradient(${bg.gradient.direction}, ${bg.gradient.from}, ${bg.gradient.to})` }
  }
  if (bg.type === 'image' && bg.imageUrl) {
    if ((bg.imageBlur ?? 0) > 0) return {} // blur handled via absolute layer
    return {
      backgroundImage: `url(${bg.imageUrl})`,
      backgroundSize: bg.imageSize ?? 'cover',
      backgroundPosition: bg.imagePosition ?? 'center',
      backgroundRepeat: bg.imageRepeat ?? 'no-repeat',
      backgroundAttachment: bg.imageFixed ? 'fixed' : undefined,
    }
  }
  return {}
}

function SectionWrapper({ sec, children }: { sec?: LandingSectionConfig; children: React.ReactNode }) {
  const bg = sec?.background
  const hasCustomBg = bg && bg.type !== 'transparent'
  const hasBlur = bg?.type === 'image' && (bg.imageBlur ?? 0) > 0 && !!bg.imageUrl
  const hasOverlay = bg?.type === 'image' && (bg.overlayOpacity ?? 0) > 0
  const hasPadding = !!sec?.padding
  const hasDivider = !!sec?.dividerBottom && sec.dividerBottom.type !== 'none'
  const textCls = sec?.textColor === 'light' ? 'text-white' : ''

  if (!sec || (!hasCustomBg && !hasPadding && !hasDivider && !sec.textColor)) {
    return <>{children}</>
  }

  const bgStyle = buildSectionBgStyle(bg)
  const paddingStyle: React.CSSProperties = hasPadding ? { paddingTop: sec!.padding!.top, paddingBottom: sec!.padding!.bottom } : {}

  return (
    <div style={{ ...bgStyle, position: 'relative' }} className={textCls}>
      {hasBlur && bg?.imageUrl && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
          <div style={{
            position: 'absolute', inset: '-10%',
            backgroundImage: `url(${bg.imageUrl})`,
            backgroundSize: bg.imageSize ?? 'cover',
            backgroundPosition: bg.imagePosition ?? 'center',
            backgroundRepeat: bg.imageRepeat ?? 'no-repeat',
            filter: `blur(${bg.imageBlur}px)`,
          }} />
        </div>
      )}
      {hasOverlay && (
        <div className="absolute inset-0 pointer-events-none"
          style={{ backgroundColor: bg!.overlayColor ?? '#000000', opacity: bg!.overlayOpacity, zIndex: 1 }} />
      )}
      <div className="relative" style={{ ...paddingStyle, zIndex: 2 }}>
        {children}
      </div>
      {hasDivider && (
        <div style={{ position: 'relative', zIndex: 3, marginTop: -1 }}>
          <SectionDividerSvg {...sec!.dividerBottom!} />
        </div>
      )}
    </div>
  )
}

// ── Título de tamanho configurável ────────────────────────────────────────────

const TITLE_SIZE_CLS: Record<string, string> = {
  sm: 'text-xl sm:text-2xl',
  md: 'text-2xl sm:text-3xl',
  lg: 'text-3xl sm:text-4xl',
  xl: 'text-4xl sm:text-5xl',
  '2xl': 'text-5xl sm:text-6xl',
}

// ── Custom block renderer ─────────────────────────────────────────────────────

function CustomBlockRenderer({ block, colors }: { block: LandingConfig['customBlocks'] extends (infer T)[] | undefined ? T : never; colors: ReturnType<typeof getColors> }) {
  const bgStyle = buildSectionBgStyle(block?.background)
  if (!block) return null

  if (block.type === 'spacer') {
    return <div style={{ height: block.config.height ?? 48 }} />
  }

  if (block.type === 'richtext') {
    return (
      <section style={bgStyle} className="py-16 px-6">
        <div className="max-w-4xl mx-auto prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: block.config.html ?? '' }} />
      </section>
    )
  }

  if (block.type === 'cta_banner') {
    return (
      <section className="py-16 px-6 text-center" style={Object.keys(bgStyle).length ? bgStyle : { background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}>
        <div className="max-w-3xl mx-auto">
          {block.config.title && <h2 className="text-3xl font-extrabold text-white mb-3">{block.config.title}</h2>}
          {block.config.subtitle && <p className="text-white/80 mb-6">{block.config.subtitle}</p>}
          {block.config.ctaText && (
            <a href={block.config.ctaUrl ?? '#'} className="inline-flex items-center gap-2 bg-white text-gray-900 font-bold px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all">
              {block.config.ctaText}
            </a>
          )}
        </div>
      </section>
    )
  }

  if (block.type === 'video') {
    return (
      <section className="py-16 px-6" style={bgStyle}>
        <div className="max-w-4xl mx-auto">
          <div className="aspect-video rounded-xl overflow-hidden shadow-lg">
            <iframe src={block.config.videoUrl ?? ''} className="w-full h-full" allowFullScreen />
          </div>
        </div>
      </section>
    )
  }

  if (block.type === 'gallery') {
    const images: string[] = block.config.images ?? []
    return (
      <section className="py-16 px-6" style={bgStyle}>
        <div className="max-w-6xl mx-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {images.map((url, i) => (
            <div key={i} className="aspect-square rounded-lg overflow-hidden">
              <img src={url} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </section>
    )
  }

  return null
}

// ── Stats Section ─────────────────────────────────────────────────────────────

function StatsSection({ cfg, colors, secCfg }: { cfg: LandingConfig | null; colors: ReturnType<typeof getColors>; secCfg?: LandingSectionConfig }) {
  const items = cfg?.statsItems ?? []
  if (!items.length) return null
  const accent = secCfg?.accentColor ?? colors.primary
  const sectionTitle = secCfg?.texts?.title
  const hasPadding = !!secCfg?.padding
  return (
    <SectionWrapper sec={secCfg}>
      <section className={`${hasPadding ? '' : 'py-16 '}px-6`}>
        <div className="max-w-6xl mx-auto">
          {sectionTitle && (
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8" style={{ color: secCfg?.headingColor ?? colors.secondary }}>{sectionTitle}</h2>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
            {items.map((stat, i) => (
              <div key={i}>
                <p className="text-4xl font-extrabold mb-1" style={{ color: accent }}>{stat.value}</p>
                <p className="text-sm font-medium" style={{ color: secCfg?.bodyColor ?? '#4b5563' }}>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SectionWrapper>
  )
}

// ── Instructors Section ───────────────────────────────────────────────────────

function InstructorsSection({ cfg, colors, secCfg }: { cfg: LandingConfig | null; colors: ReturnType<typeof getColors>; secCfg?: LandingSectionConfig }) {
  const instructors = cfg?.instructors ?? []
  if (!instructors.length) return null
  const accent = secCfg?.accentColor ?? colors.primary
  const hColor = secCfg?.headingColor ?? colors.secondary
  const sectionTitle = secCfg?.texts?.title ?? 'A Nossa Equipa'
  const hasPadding = !!secCfg?.padding
  return (
    <SectionWrapper sec={secCfg}>
      <section className={`${hasPadding ? '' : 'py-20 '}px-6`}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-extrabold text-center mb-12" style={{ color: hColor }}>{sectionTitle}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-8">
            {instructors.map(instr => (
              <div key={instr.id} className="text-center">
                {instr.fotoUrl
                  ? <img src={instr.fotoUrl} alt={instr.nome} className="w-24 h-24 rounded-full object-cover mx-auto mb-3 border-4 border-gray-100 shadow" />
                  : <div className="w-24 h-24 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: accent + '20' }}>
                      <Users className="w-10 h-10" style={{ color: accent }} />
                    </div>
                }
                <p className="font-bold" style={{ color: secCfg?.headingColor ?? '#111827' }}>{instr.nome}</p>
                {instr.cargo && <p className="text-sm" style={{ color: secCfg?.bodyColor ?? '#6b7280' }}>{instr.cargo}</p>}
                {instr.bio && <p className="text-xs mt-1" style={{ color: secCfg?.bodyColor ?? '#9ca3af' }}>{instr.bio}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>
    </SectionWrapper>
  )
}

// ── WhatsApp Floating Button ──────────────────────────────────────────────────

function WhatsAppButton({ cfg, colors }: { cfg: LandingConfig | null; colors: ReturnType<typeof getColors> }) {
  const wa = cfg?.whatsappConfig
  if (!wa?.enabled || !wa.number) return null
  const number = wa.number.replace(/\D/g, '')
  const msg = encodeURIComponent(wa.message ?? '')
  const href = `https://wa.me/${number}${msg ? `?text=${msg}` : ''}`
  const isRight = (wa.position ?? 'bottom-right') === 'bottom-right'

  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className={`fixed bottom-6 z-50 flex items-center gap-2 text-white font-semibold px-4 py-3 rounded-full shadow-xl hover:shadow-2xl transition-all hover:scale-105 ${isRight ? 'right-6' : 'left-6'}`}
      style={{ backgroundColor: '#25D366' }}>
      <MessageSquare className="w-5 h-5" />
      {wa.label && <span className="text-sm">{wa.label}</span>}
    </a>
  )
}

export function LandingPageCompleta({ school, escolaSlug }: { school: PortalInfo; escolaSlug: string }) {
  const [slideIdx, setSlideIdx] = useState(0)
  const formRef = useRef<HTMLDivElement>(null)
  // Usar rich slides da config; fallback para slideshowImages legado
  const cfg = school.landingConfig
  const richSlides = cfg?.slides ?? []
  const slides = richSlides.length > 0
    ? richSlides
    : (school.slideshowImages ?? []).map(img => ({ imageUrl: img.url, title: img.titulo, subtitle: img.descricao }))
  const colors = getColors(cfg)
  const mostrarFormulario = school.landingShowForm
  const orderedSections = getOrderedSections(cfg)

  const { data: categoriasLicenca } = useCategoriasLicenca()
  const categoriasMap = useMemo(() => buildCategoriaLicencaMap(categoriasLicenca), [categoriasLicenca])

  // Favicon dinâmico por escola
  useEffect(() => {
    if (!school.faviconUrl) return
    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = school.faviconUrl
    return () => { link!.href = '/favicon.ico' }
  }, [school.faviconUrl])

  // Timer por slide (usa duration configurável ou 5000ms por defeito)
  useEffect(() => {
    if (!slides.length) return
    const duration = (slides[slideIdx] as any)?.duration ?? 5000
    const t = setTimeout(() => setSlideIdx(i => (i + 1) % slides.length), duration)
    return () => clearTimeout(t)
  }, [slides.length, slideIdx])

  function scrollToForm() {
    const el = document.getElementById('inscricao')
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Botões globais: novo sistema heroButtons com fallback para os campos legacy
  const globalButtons: LandingHeroButton[] = (() => {
    const configured = (cfg?.heroButtons ?? []).filter(b => b.visible)
    if (configured.length > 0) return [...configured].sort((a, b) => a.order - b.order)
    // Fallback legacy
    if (cfg?.heroHideButtons) return []
    const btns: LandingHeroButton[] = []
    if (mostrarFormulario) {
      btns.push({ id: 'legacy-primary', text: cfg?.heroCtaText || 'Quero inscrever-me', url: '#form', style: 'primary', visible: true, order: 0 })
    }
    btns.push({ id: 'legacy-secondary', text: cfg?.heroSecondaryCtaText || 'Já tenho conta', url: `/${escolaSlug}/aluno`, style: 'secondary', visible: true, order: 1 })
    return btns
  })()

  const sectionProps: SectionProps = { school, cfg, colors, escolaSlug, scrollToForm, mostrarFormulario, categoriasMap }

  const getSec = (key: string) => orderedSections.find(s => s.key === key)

  const SECTION_MAP: Record<string, React.ReactNode> = {
    categories:   <CategoriesSection key="categories" {...sectionProps} secCfg={getSec('categories')} />,
    howItWorks:   <HowItWorksSection key="howItWorks" {...sectionProps} secCfg={getSec('howItWorks')} />,
    stats:        <StatsSection key="stats" cfg={cfg} colors={colors} secCfg={getSec('stats')} />,
    about:        <AboutSection key="about" {...sectionProps} secCfg={getSec('about')} />,
    instructors:  <InstructorsSection key="instructors" cfg={cfg} colors={colors} secCfg={getSec('instructors')} />,
    form:         <FormSection key="form" {...sectionProps} formRef={formRef} secCfg={getSec('form')} />,
    testimonials: <TestimonialsSection key="testimonials" {...sectionProps} secCfg={getSec('testimonials')} />,
    payment:      <PaymentSection key="payment" {...sectionProps} secCfg={getSec('payment')} />,
    faq:          <FaqSection key="faq" {...sectionProps} secCfg={getSec('faq')} />,
    contact:      <ContactSection key="contact" {...sectionProps} secCfg={getSec('contact')} />,
  }

  // Social links do rodapé
  const socialLinks = cfg?.socialLinks || {}
  const hasSocial = Object.values(socialLinks).some(v => v)
  const footerLinks = cfg?.footerLinks || []

  return (
    <div className="min-h-screen flex flex-col bg-white">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-200 px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {school.logoUrl
              ? <img src={school.logoUrl} alt={school.name} className="w-auto object-contain shrink-0"
                  style={{ height: cfg?.logoSize ? `${cfg.logoSize}px` : '36px' }} />
              : <div className="rounded-lg flex items-center justify-center shrink-0"
                  style={{ width: cfg?.logoSize ? `${cfg.logoSize}px` : '36px', height: cfg?.logoSize ? `${cfg.logoSize}px` : '36px', backgroundColor: colors.primary }}>
                  <GraduationCap className="w-5 h-5 text-white" />
                </div>
            }
            {!school.logoUrl && (
              <span className="text-base font-bold text-gray-900 truncate hidden sm:block">{school.name}</span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link href={`/${escolaSlug}/aluno`}
              className="hidden md:inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors px-3 py-1.5 rounded-lg hover:bg-gray-100">
              <GraduationCap className="w-4 h-4" /> Área do Aluno
            </Link>
            {mostrarFormulario && (
              <button onClick={scrollToForm}
                className="inline-flex items-center gap-1.5 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-all shadow-sm hover:shadow-md"
                style={{ backgroundColor: colors.primary }}>
                Inscrever-me
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ── Google Fonts ────────────────────────────────────────────────────── */}
      {cfg?.fontConfig && (
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=${encodeURIComponent(cfg.fontConfig.heading)}:wght@400;600;700;800&family=${encodeURIComponent(cfg.fontConfig.body)}:wght@400;500;600&display=swap');
          body { font-family: '${cfg.fontConfig.body}', sans-serif; }
          h1,h2,h3,h4 { font-family: '${cfg.fontConfig.heading}', sans-serif; }
        `}</style>
      )}

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden" style={{ minHeight: 420, backgroundColor: colors.secondary }}>
        {slides.length > 0 ? (
          <>
            {slides.map((slide, i) => {
              const s = slide as LandingSlide
              return (
                <img key={i} src={s.imageUrl ?? ''} alt={s.title || ''}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === slideIdx ? 'opacity-100' : 'opacity-0'}`} />
              )
            })}
            {/* Overlay por slide */}
            {(() => {
              const s = slides[slideIdx] as LandingSlide
              const oc = s?.overlayColor ?? '#000000'
              const oo = s?.overlayOpacity ?? 0.4
              return <div className="absolute inset-0" style={{ backgroundColor: oc, opacity: oo }} />
            })()}
            {/* Texto do slide actual */}
            {(() => {
              const s = slides[slideIdx] as LandingSlide
              const hPos = s?.textPosition ?? 'center'
              const vPos = s?.textVertical ?? 'middle'
              const titleSizeCls = TITLE_SIZE_CLS[s?.titleSize ?? 'xl'] ?? TITLE_SIZE_CLS.xl
              return (s?.title || s?.subtitle) ? (
                <div className={`absolute inset-0 z-5 flex px-8 ${vPos === 'top' ? 'items-start pt-16' : vPos === 'bottom' ? 'items-end pb-24' : 'items-center'} ${hPos === 'left' ? 'justify-start' : hPos === 'right' ? 'justify-end' : 'justify-center'}`}>
                  <div className={`max-w-2xl ${hPos === 'center' ? 'text-center' : hPos === 'right' ? 'text-right' : 'text-left'}`}>
                    {s.title && <p className={`font-extrabold drop-shadow-lg ${titleSizeCls}`} style={{ color: s.titleColor ?? '#ffffff' }}>{s.title}</p>}
                    {s.subtitle && <p className="text-lg drop-shadow mt-2" style={{ color: s.subtitleColor ?? '#f0f0f0' }}>{s.subtitle}</p>}
                    {s.cta?.text && (
                      <a href={s.cta.url || '#'} className={`inline-flex items-center gap-2 mt-4 px-6 py-3 rounded-xl font-bold shadow-lg transition-all hover:scale-105 ${s.cta.style === 'outline' ? 'bg-transparent border-2 border-white text-white' : s.cta.style === 'secondary' ? 'bg-white text-gray-900' : 'text-white'}`}
                        style={s.cta.style !== 'outline' && s.cta.style !== 'secondary' ? { backgroundColor: colors.primary } : {}}>
                        {s.cta.text}
                      </a>
                    )}
                  </div>
                </div>
              ) : null
            })()}
            {slides.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                {slides.map((_, i) => (
                  <button key={i} onClick={() => setSlideIdx(i)}
                    className={`h-2 rounded-full transition-all ${i === slideIdx ? 'w-8 bg-white' : 'w-2 bg-white/50'}`} />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }} />
        )}
        {(() => {
          const hasSlides = slides.length > 0
          const showText  = !hasSlides || (cfg?.heroShowTextWithSlides === true)

          // Botões do slide actual: ctaButtons > slide.cta legacy > global
          const currentSlide = hasSlides ? slides[slideIdx] as LandingSlide : null
          let activeButtons: LandingHeroButton[] = []
          if (currentSlide?.slideHideButtons) {
            activeButtons = []
          } else if (currentSlide?.ctaButtons?.length) {
            activeButtons = [...currentSlide.ctaButtons].filter(b => b.visible).sort((a, b) => a.order - b.order)
          } else if (currentSlide?.cta?.text) {
            // Legacy fallback: single cta per slide
            activeButtons = [{ id: 'slide-cta', text: currentSlide.cta.text, url: currentSlide.cta.url || '#', style: (currentSlide.cta.style ?? 'primary') as any, visible: true, order: 0 }]
          } else {
            activeButtons = globalButtons
          }

          if (!showText && !activeButtons.length) return null

          const renderButton = (btn: LandingHeroButton) => {
            const isForm = btn.url === '#form' || btn.url === '#inscricao'
            const cls = `w-full sm:w-auto inline-flex items-center justify-center gap-2 font-bold px-8 py-3.5 rounded-xl shadow-lg transition-all hover:shadow-xl hover:scale-105 ${
              btn.style === 'primary'   ? 'text-white' :
              btn.style === 'secondary' ? 'bg-white text-gray-900' :
              btn.style === 'outline'   ? 'bg-transparent border-2 border-white text-white' :
                                          'bg-transparent text-white underline'
            }`
            const style = btn.style === 'primary' ? { backgroundColor: colors.primary } : {}
            if (isForm) {
              return <button key={btn.id} onClick={scrollToForm} className={cls} style={style}>{btn.text}</button>
            }
            return <a key={btn.id} href={btn.url} target={btn.newTab ? '_blank' : undefined} rel={btn.newTab ? 'noopener noreferrer' : undefined} className={cls} style={style}>{btn.text}</a>
          }

          return (
            <div className={`relative z-10 flex flex-col items-center justify-center text-center text-white px-6 ${showText ? 'py-20 md:py-28' : 'py-8 md:py-14'}`}>
              {showText && (
                <>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-5 drop-shadow-lg leading-tight max-w-4xl">
                    {school.landingHeroTitle || 'Obtenha a sua carta de condução'}
                  </h1>
                  <p className="text-base sm:text-lg md:text-xl text-white/85 mb-10 max-w-2xl drop-shadow leading-relaxed">
                    {school.landingHeroSubtitle || `Inscreva-se em ${school.name} e dê o primeiro passo rumo à sua independência.`}
                  </p>
                </>
              )}
              {activeButtons.length > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 w-full sm:w-auto">
                  {activeButtons.map(renderButton)}
                </div>
              )}
            </div>
          )
        })()}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 animate-bounce hidden md:block">
          <ChevronDown className="w-6 h-6 text-white/60" />
        </div>
      </section>

      {/* ── Secções dinâmicas + custom blocks intercalados ─────────────────── */}
      {orderedSections.filter(s => s.visible).map(s => {
        const sectionNode = SECTION_MAP[s.key]
        const blocksAfter = (cfg?.customBlocks ?? []).filter(b => b.afterSection === s.key)
        return (
          <div key={s.key}>
            {sectionNode}
            {blocksAfter.map(block => (
              <CustomBlockRenderer key={block.id} block={block} colors={colors} />
            ))}
          </div>
        )
      })}
      {/* Blocos sem afterSection (início) */}
      {(cfg?.customBlocks ?? []).filter(b => !b.afterSection).map(block => (
        <CustomBlockRenderer key={block.id} block={block} colors={colors} />
      ))}

      {/* ── WhatsApp flutuante ──────────────────────────────────────────────── */}
      <WhatsAppButton cfg={cfg} colors={colors} />

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <footer className="text-white pt-12 pb-6 px-6" style={{ backgroundColor: colors.secondary }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-10">
            {/* Col 1: Marca */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                {school.logoUrl
                  ? <img src={school.logoUrl} alt={school.name} className="w-auto object-contain brightness-0 invert"
                      style={{ height: cfg?.logoSize ? `${Math.round(cfg.logoSize * 1.1)}px` : '40px' }} />
                  : <div className="rounded-lg flex items-center justify-center"
                      style={{ width: '40px', height: '40px', backgroundColor: colors.primary }}>
                      <GraduationCap className="w-5 h-5 text-white" />
                    </div>
                }
                {!school.logoUrl && (
                  <span className="font-bold text-lg text-white">{school.name}</span>
                )}
              </div>
              <p className="text-sm text-white/60 leading-relaxed mb-4">
                {cfg?.footerDescription || 'Escola de condução certificada em Cabo Verde.'}
              </p>
              {hasSocial && (
                <div className="flex gap-3">
                  {Object.entries(socialLinks).map(([platform, url]) => url ? (
                    <a key={platform} href={url} target="_blank" rel="noopener noreferrer"
                      className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors text-white/70 hover:text-white">
                      <SocialIcon platform={platform} className="w-4 h-4" />
                    </a>
                  ) : null)}
                </div>
              )}
            </div>

            {/* Col 2: Links rápidos */}
            <div>
              <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Acesso</h4>
              <div className="space-y-2.5">
                <Link href={`/${escolaSlug}/aluno`} className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
                  <GraduationCap className="w-4 h-4" /> Área do Aluno
                </Link>
                <Link href={`/${escolaSlug}/admin`} className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
                  <Shield className="w-4 h-4" /> Área Administrativa
                </Link>
                {footerLinks.map((link, i) => (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
                    <ArrowRight className="w-3 h-3" /> {link.label}
                  </a>
                ))}
              </div>
            </div>

            {/* Col 3: Contacto */}
            {(school.address || school.phone || school.email) && (
              <div>
                <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Contacto</h4>
                <div className="space-y-2.5">
                  {school.address && (
                    <a href={`https://maps.google.com/?q=${encodeURIComponent(school.address)}`} target="_blank" rel="noopener"
                      className="flex items-start gap-2 text-sm text-white/60 hover:text-white transition-colors">
                      <MapPin className="w-4 h-4 shrink-0 mt-0.5" /> {school.address}
                    </a>
                  )}
                  {school.phone && (
                    <a href={`tel:${school.phone}`} className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
                      <Phone className="w-4 h-4 shrink-0" /> {school.phone}
                    </a>
                  )}
                  {school.email && (
                    <a href={`mailto:${school.email}`} className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
                      <Mail className="w-4 h-4 shrink-0" /> {school.email}
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Col 4: Horário */}
            {cfg?.footerSchedule && (
              <div>
                <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Horário</h4>
                <p className="text-sm text-white/60 whitespace-pre-line leading-relaxed">{cfg.footerSchedule}</p>
              </div>
            )}
          </div>

          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/40">
              &copy; {new Date().getFullYear()} {school.name}. Todos os direitos reservados.
            </p>
            <a href="https://salweb.cv" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs text-white/30">
              Desenvolvido por
              <Image src="/images/logos/salweb-white.png" alt="SALWEB.cv" width={72} height={20} unoptimized
                style={{ height: '16px', width: 'auto', display: 'block' }} />
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}

// ── Portal básico (landingPageEnabled=false) ──────────────────────────────────

export function PortalBasico({ school, escolaSlug }: { school: PortalInfo; escolaSlug: string }) {
  const [slideIdx, setSlideIdx] = useState(0)
  const slides = school.slideshowImages || []

  useEffect(() => {
    if (!school.faviconUrl) return
    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link) }
    link.href = school.faviconUrl
    return () => { link!.href = '/favicon.ico' }
  }, [school.faviconUrl])

  useEffect(() => {
    if (!slides.length) return
    const t = setInterval(() => setSlideIdx(i => (i + 1) % slides.length), 5000)
    return () => clearInterval(t)
  }, [slides.length])

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-gray-200 px-6 py-3">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          {school.logoUrl
            ? <img src={school.logoUrl} alt={school.name} className="h-9 w-auto object-contain" />
            : <div className="w-9 h-9 bg-strada-600 rounded-lg flex items-center justify-center shrink-0"><GraduationCap className="w-5 h-5 text-white" /></div>
          }
          {!school.logoUrl && (
            <span className="text-lg font-bold text-gray-900 truncate">{school.name}</span>
          )}
        </div>
      </header>

      {slides.length > 0 ? (
        <div className="relative h-64 md:h-96 overflow-hidden bg-gray-900">
          {slides.map((img, i) => (
            <img key={i} src={img.url} alt={img.titulo || ''}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === slideIdx ? 'opacity-100' : 'opacity-0'}`} />
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          {slides[slideIdx]?.titulo && (
            <div className="absolute bottom-10 left-0 right-0 text-center text-white px-6">
              <p className="text-lg font-semibold drop-shadow">{slides[slideIdx].titulo}</p>
              {slides[slideIdx].descricao && <p className="text-sm text-white/80 mt-1 drop-shadow">{slides[slideIdx].descricao}</p>}
            </div>
          )}
          {slides.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {slides.map((_, i) => (
                <button key={i} onClick={() => setSlideIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${i === slideIdx ? 'w-6 bg-white' : 'w-1.5 bg-white/50'}`} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="h-48 bg-gradient-to-br from-strada-600 to-strada-700 flex items-center justify-center">
          <div className="text-center text-white px-6">
            <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-70" />
            <h2 className="text-2xl font-bold">{school.name}</h2>
          </div>
        </div>
      )}

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Bem-vindo</h1>
          <p className="text-gray-500">Seleccione a sua área de acesso</p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <Link href={`/${escolaSlug}/aluno`}
            className="group flex flex-col items-center text-center p-8 border-2 border-gray-200 rounded-2xl hover:border-strada-500 hover:bg-strada-50 transition-all">
            <div className="w-14 h-14 bg-strada-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-strada-200 transition-colors">
              <GraduationCap className="w-7 h-7 text-strada-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Área do Aluno</h2>
            <p className="text-gray-500 text-sm mb-5 flex-1">Aceda aos seus testes, resultados e conteúdos de aprendizagem.</p>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-strada-600 group-hover:gap-3 transition-all">
              Entrar <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
          <Link href={`/${escolaSlug}/admin`}
            className="group flex flex-col items-center text-center p-8 border-2 border-gray-200 rounded-2xl hover:border-gray-700 hover:bg-gray-50 transition-all">
            <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-gray-200 transition-colors">
              <Shield className="w-7 h-7 text-gray-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Área Administrativa</h2>
            <p className="text-gray-500 text-sm mb-5 flex-1">Acesso exclusivo para administradores e instrutores.</p>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 group-hover:gap-3 transition-all">
              Entrar <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        </div>
        {(school.address || school.phone || school.email) && (
          <div className="mt-12 pt-8 border-t border-gray-200 flex flex-wrap justify-center gap-6 text-sm text-gray-400">
            {school.address && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 shrink-0" /> {school.address}</span>}
            {school.phone   && <span className="flex items-center gap-1.5"><Phone className="w-4 h-4 shrink-0" /> {school.phone}</span>}
            {school.email   && <span className="flex items-center gap-1.5"><Mail className="w-4 h-4 shrink-0" /> {school.email}</span>}
          </div>
        )}
      </main>

      <footer className="text-center text-xs text-gray-400 py-4 border-t border-gray-100">
        STRADA · Plataforma de escolas de condução
      </footer>
    </div>
  )
}

// ── Página principal ──────────────────────────────────────────────────────────

export default function EscolaLandingPage() {
  const { escolaSlug } = useParams<{ escolaSlug: string }>()
  const [school, setSchool] = useState<PortalInfo | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (escolaSlug) {
      localStorage.setItem('strada_tenant_slug', escolaSlug)
    }
    api.get(`/schools/portal/${escolaSlug}`)
      .then(r => setSchool(r.data))
      .catch(() => setSchool(null))
      .finally(() => setLoading(false))
  }, [escolaSlug])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-strada-600" />
      </div>
    )
  }

  if (!school) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="text-center">
          <GraduationCap className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Escola não encontrada</h1>
          <p className="text-gray-500">O endereço &quot;{escolaSlug}&quot; não corresponde a nenhuma escola registada.</p>
        </div>
      </div>
    )
  }

  if (school.landingPageEnabled) {
    return <LandingPageCompleta school={school} escolaSlug={escolaSlug} />
  }

  return <PortalBasico school={school} escolaSlug={escolaSlug} />
}
